// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  AlertOctagon,
  ArrowRight,
  Building2,
  Mail,
  ShieldCheck,
  Users,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Badge, Skeleton, formatDateTimeVn } from "@chotca/ui";
import type {
  AbuseReport,
  AdminClaimInvitationItem,
  AdminShadowOrgWithApplicantsItem,
  CancellationRequestDto,
  OrgKycSubmissionAdmin,
  UserIdentityAdmin,
  WorkerKycSubmissionAdmin,
} from "@chotca/config";
import {
  useAdminAbuseReports,
  useAdminAllClaimInvitations,
  useAdminCancellationRequests,
  useAdminIdentityList,
  useAdminOrgKycList,
  useAdminShadowOrgsWithApplicants,
  useAdminTodo,
  useAdminWorkerKycList,
} from "@chotca/api-client";

const PREVIEW_LIMIT = 5;

export function TodoClient() {
  const t = useTranslations("admin");

  const todo = useAdminTodo();
  const identity = useAdminIdentityList();
  const workerKyc = useAdminWorkerKycList();
  const orgKyc = useAdminOrgKycList();
  const abuse = useAdminAbuseReports({
    status: "PENDING",
    limit: PREVIEW_LIMIT,
  });
  const claims = useAdminAllClaimInvitations({
    status: "PENDING",
    limit: PREVIEW_LIMIT,
  });
  const cancellations = useAdminCancellationRequests({
    status: "PENDING",
    limit: PREVIEW_LIMIT,
  });
  const shadowOrgs = useAdminShadowOrgsWithApplicants({ limit: PREVIEW_LIMIT });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("todoTitle")}</h1>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Định danh tài khoản"
          icon={ShieldCheck}
          tone="warning"
          count={identity.data?.total}
          seeAllHref="/kyc/identity"
          seeAllLabel={t("todoSeeAll")}
          isLoading={identity.isLoading}
          isError={identity.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => identity.refetch()}
          emptyLabel="Không có hồ sơ định danh chờ duyệt."
          emptyVariant="workerKyc"
          items={(identity.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(s: UserIdentityAdmin) => (
            <PreviewRow
              key={s.userId}
              to={`/kyc/identity/${s.userId}`}
              title={s.fullName}
              subtitle={s.userPhone ?? "—"}
              meta={formatDateTimeVn(new Date(s.submittedAt))}
            />
          )}
        />

        <Section
          title={t("todoSectionWorkerKyc")}
          icon={ShieldCheck}
          tone="warning"
          count={todo.data?.pendingWorkerKyc}
          seeAllHref="/kyc/workers"
          seeAllLabel={t("todoSeeAll")}
          isLoading={workerKyc.isLoading}
          isError={workerKyc.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => workerKyc.refetch()}
          emptyLabel={t("todoSectionEmptyWorkerKyc")}
          emptyVariant="workerKyc"
          items={(workerKyc.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(s: WorkerKycSubmissionAdmin) => (
            <PreviewRow
              key={s.id}
              to={`/kyc/workers/${s.id}`}
              title={s.fullName}
              subtitle={s.userPhone ?? "—"}
              meta={formatDateTimeVn(new Date(s.submittedAt))}
            />
          )}
        />

        <Section
          title={t("todoSectionOrgKyc")}
          icon={Building2}
          tone="warning"
          count={todo.data?.pendingOrgKyc}
          seeAllHref="/kyc/orgs"
          seeAllLabel={t("todoSeeAll")}
          isLoading={orgKyc.isLoading}
          isError={orgKyc.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => orgKyc.refetch()}
          emptyLabel={t("todoSectionEmptyOrgKyc")}
          emptyVariant="orgKyc"
          items={(orgKyc.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(s: OrgKycSubmissionAdmin) => (
            <PreviewRow
              key={s.id}
              to={`/kyc/orgs/${s.id}`}
              title={s.legalName}
              subtitle={`MST ${s.taxCode}`}
              meta={formatDateTimeVn(new Date(s.submittedAt))}
            />
          )}
        />

        <Section
          title={t("todoSectionAbuse")}
          icon={AlertOctagon}
          tone="danger"
          count={todo.data?.openAbuseReports}
          seeAllHref="/abuse"
          seeAllLabel={t("todoSeeAll")}
          isLoading={abuse.isLoading}
          isError={abuse.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => abuse.refetch()}
          emptyLabel={t("todoSectionEmptyAbuse")}
          emptyVariant="abuse"
          items={(abuse.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(r: AbuseReport) => (
            <PreviewRow
              key={r.id}
              to="/abuse"
              title={r.reason}
              subtitle={`${t(`abuseTarget_${r.targetType}`)} · ${r.targetId.slice(0, 8)}`}
              meta={formatDateTimeVn(new Date(r.createdAt))}
              titleClamp
            />
          )}
        />

        <Section
          title={t("todoSectionClaim")}
          icon={Mail}
          tone="default"
          count={todo.data?.pendingClaimInvitations}
          seeAllHref="/claim-invitations"
          seeAllLabel={t("todoSeeAll")}
          isLoading={claims.isLoading}
          isError={claims.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => claims.refetch()}
          emptyLabel={t("todoSectionEmptyClaim")}
          emptyVariant="claim"
          items={(claims.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(c: AdminClaimInvitationItem) => (
            <PreviewRow
              key={c.id}
              to={`/orgs/${c.orgId}`}
              title={c.orgName}
              subtitle={c.email}
              meta={formatDateTimeVn(new Date(c.expiresAt))}
            />
          )}
        />

        <Section
          title={t("todoSectionCancellation")}
          icon={XCircle}
          tone="danger"
          count={todo.data?.pendingCancellationRequests}
          seeAllHref="/cancellations"
          seeAllLabel={t("todoSeeAll")}
          isLoading={cancellations.isLoading}
          isError={cancellations.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => cancellations.refetch()}
          emptyLabel={t("todoSectionEmptyCancellation")}
          emptyVariant="cancellation"
          items={(cancellations.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(r: CancellationRequestDto) => (
            <PreviewRow
              key={r.id}
              to="/cancellations"
              title={r.reason}
              subtitle={`Invite ${r.inviteId.slice(0, 8)} · ${r.requesterRole}`}
              meta={formatDateTimeVn(new Date(r.createdAt))}
              titleClamp
            />
          )}
        />

        <Section
          title={t("todoSectionShadowOrg")}
          icon={Users}
          tone="default"
          count={todo.data?.shadowOrgsWithApplicants}
          seeAllHref="/shadow-orgs"
          seeAllLabel={t("todoSeeAll")}
          isLoading={shadowOrgs.isLoading}
          isError={shadowOrgs.isError}
          errorLabel={t("todoSectionError")}
          retryLabel={t("todoRetry")}
          onRetry={() => shadowOrgs.refetch()}
          emptyLabel={t("todoSectionEmptyShadowOrg")}
          emptyVariant="shadowOrg"
          items={(shadowOrgs.data?.items ?? []).slice(0, PREVIEW_LIMIT)}
          renderItem={(o: AdminShadowOrgWithApplicantsItem) => (
            <PreviewRow
              key={o.orgId}
              to={`/orgs/${o.orgId}`}
              title={o.name}
              subtitle={t("shadowOrgPendingApplicantsValue", {
                count: o.pendingApplicantCount,
              })}
              meta={formatDateTimeVn(new Date(o.latestAppliedAt))}
            />
          )}
        />
      </div>
    </div>
  );
}

type IllustrationVariant =
  | "workerKyc"
  | "orgKyc"
  | "abuse"
  | "claim"
  | "cancellation"
  | "shadowOrg";

function EmptySectionIllustration({
  variant,
}: {
  variant: IllustrationVariant;
}) {
  if (variant === "workerKyc") {
    return (
      <svg
        width="84"
        height="76"
        viewBox="0 0 84 76"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="42" cy="38" r="30" fill="#FEF3C7" opacity="0.55" />
        {/* ID card */}
        <rect
          x="14"
          y="20"
          width="52"
          height="36"
          rx="8"
          fill="white"
          stroke="#FCD34D"
          strokeWidth="1.5"
        />
        {/* Avatar circle */}
        <circle
          cx="30"
          cy="35"
          r="8"
          fill="#FEF9C3"
          stroke="#FCD34D"
          strokeWidth="1"
        />
        {/* Person in circle */}
        <circle cx="30" cy="32" r="3" fill="#FCD34D" />
        <path
          d="M23 43 Q23 39 30 39 Q37 39 37 43"
          stroke="#FCD34D"
          strokeWidth="1.25"
          strokeLinecap="round"
          fill="none"
        />
        {/* Text lines */}
        <rect x="44" y="28" width="16" height="3.5" rx="1.75" fill="#E8E8E4" />
        <rect
          x="44"
          y="34.5"
          width="12"
          height="3.5"
          rx="1.75"
          fill="#F0F0EC"
        />
        <rect x="44" y="41" width="14" height="3.5" rx="1.75" fill="#F0F0EC" />
        {/* Check badge */}
        <circle
          cx="56"
          cy="50"
          r="10"
          fill="#DCFCE7"
          stroke="white"
          strokeWidth="2.5"
        />
        <path
          d="M51 50 L54.5 53.5 L61 46"
          stroke="#16a34a"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Lime accents */}
        <circle cx="18" cy="15" r="4" fill="#32ffaa" opacity="0.5" />
        <circle cx="27" cy="10" r="2.5" fill="#32ffaa" opacity="0.25" />
      </svg>
    );
  }

  if (variant === "orgKyc") {
    return (
      <svg
        width="84"
        height="76"
        viewBox="0 0 84 76"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="42" cy="40" r="30" fill="#FEF3C7" opacity="0.55" />
        {/* Building body */}
        <rect
          x="22"
          y="34"
          width="40"
          height="26"
          rx="3"
          fill="white"
          stroke="#FCD34D"
          strokeWidth="1.5"
        />
        {/* Roof */}
        <path
          d="M16 36 L42 16 L68 36 Z"
          fill="#FEF9C3"
          stroke="#FCD34D"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Windows */}
        <rect
          x="27"
          y="40"
          width="9"
          height="9"
          rx="2"
          fill="#FEF9C3"
          stroke="#FCD34D"
          strokeWidth="1"
        />
        <rect
          x="48"
          y="40"
          width="9"
          height="9"
          rx="2"
          fill="#FEF9C3"
          stroke="#FCD34D"
          strokeWidth="1"
        />
        {/* Door */}
        <rect x="37" y="47" width="10" height="13" rx="2" fill="#FEF9C3" />
        {/* Check badge */}
        <circle
          cx="60"
          cy="26"
          r="10"
          fill="#DCFCE7"
          stroke="white"
          strokeWidth="2.5"
        />
        <path
          d="M55 26 L58.5 29.5 L65 22"
          stroke="#16a34a"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Lime accent */}
        <circle cx="20" cy="14" r="3.5" fill="#32ffaa" opacity="0.45" />
      </svg>
    );
  }

  if (variant === "cancellation") {
    return (
      <svg
        width="84"
        height="76"
        viewBox="0 0 84 76"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="42" cy="38" r="30" fill="#FFF1F2" opacity="0.55" />
        {/* Calendar body */}
        <rect
          x="18"
          y="22"
          width="48"
          height="40"
          rx="6"
          fill="white"
          stroke="#FDA4AF"
          strokeWidth="1.5"
        />
        {/* Calendar header */}
        <path
          d="M18 30 C18 25.6 21.6 22 26 22 L58 22 C62.4 22 66 25.6 66 30 L66 32 L18 32 Z"
          fill="#FFE4E6"
        />
        {/* Hangers */}
        <rect x="28" y="16" width="3.5" height="10" rx="1.75" fill="#FDA4AF" />
        <rect
          x="52.5"
          y="16"
          width="3.5"
          height="10"
          rx="1.75"
          fill="#FDA4AF"
        />
        {/* X mark */}
        <path
          d="M35 41 L49 55 M49 41 L35 55"
          stroke="#f43f5e"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        {/* Lime accents */}
        <circle cx="21" cy="15" r="4" fill="#32ffaa" opacity="0.45" />
        <circle cx="30" cy="10" r="2.5" fill="#32ffaa" opacity="0.25" />
      </svg>
    );
  }

  if (variant === "shadowOrg") {
    return (
      <svg
        width="84"
        height="76"
        viewBox="0 0 84 76"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="42" cy="40" r="30" fill="#ECFCCB" opacity="0.6" />
        {/* Storefront */}
        <rect
          x="22"
          y="34"
          width="40"
          height="26"
          rx="3"
          fill="white"
          stroke="#A3E635"
          strokeWidth="1.5"
        />
        {/* Awning */}
        <path
          d="M18 34 L22 26 L62 26 L66 34 Z"
          fill="#ECFCCB"
          stroke="#A3E635"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Door */}
        <rect x="37" y="46" width="10" height="14" rx="1.5" fill="#ECFCCB" />
        {/* Person / applicant badge */}
        <circle
          cx="58"
          cy="50"
          r="11"
          fill="#32ffaa"
          opacity="0.9"
          stroke="white"
          strokeWidth="2.5"
        />
        <circle cx="58" cy="47" r="3" fill="white" />
        <path d="M52.5 56 Q52.5 51 58 51 Q63.5 51 63.5 56" fill="white" />
        {/* Lime accents */}
        <circle cx="20" cy="15" r="4" fill="#32ffaa" opacity="0.45" />
        <circle cx="29" cy="10" r="2.5" fill="#32ffaa" opacity="0.25" />
      </svg>
    );
  }

  if (variant === "abuse") {
    return (
      <svg
        width="84"
        height="76"
        viewBox="0 0 84 76"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="42" cy="38" r="30" fill="#FFF1F2" opacity="0.55" />
        {/* Shield body */}
        <path
          d="M42 12 L62 21 L62 42 C62 54 42 64 42 64 C42 64 22 54 22 42 L22 21 Z"
          fill="white"
          stroke="#FDA4AF"
          strokeWidth="1.5"
        />
        {/* Inner shield fill */}
        <path
          d="M42 18 L56 25 L56 42 C56 51 42 58 42 58 C42 58 28 51 28 42 L28 25 Z"
          fill="#FFF1F2"
          opacity="0.6"
        />
        {/* Large check */}
        <path
          d="M32 40 L38 46 L52 30"
          stroke="#22c55e"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Lime accents */}
        <circle cx="21" cy="16" r="4" fill="#32ffaa" opacity="0.45" />
        <circle cx="30" cy="10" r="2.5" fill="#32ffaa" opacity="0.25" />
        <circle cx="14" cy="25" r="2" fill="#32ffaa" opacity="0.2" />
      </svg>
    );
  }

  return (
    <svg
      width="84"
      height="76"
      viewBox="0 0 84 76"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="42" cy="38" r="30" fill="#EEF2FF" opacity="0.6" />
      {/* Envelope body */}
      <rect
        x="12"
        y="22"
        width="56"
        height="38"
        rx="7"
        fill="white"
        stroke="#C4B5FD"
        strokeWidth="1.5"
      />
      {/* Envelope flap */}
      <path
        d="M12 30 L42 48 L68 30"
        stroke="#C4B5FD"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Bottom fold lines */}
      <path
        d="M12 60 L30 46"
        stroke="#E0D9FB"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <path
        d="M68 60 L50 46"
        stroke="#E0D9FB"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Check badge */}
      <circle
        cx="58"
        cy="52"
        r="10"
        fill="#DCFCE7"
        stroke="white"
        strokeWidth="2.5"
      />
      <path
        d="M53 52 L56.5 55.5 L63 48"
        stroke="#16a34a"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Lime accents */}
      <circle cx="17" cy="16" r="4" fill="#32ffaa" opacity="0.45" />
      <circle cx="26" cy="10" r="2.5" fill="#32ffaa" opacity="0.25" />
    </svg>
  );
}

type Tone = "default" | "warning" | "danger";

const TONE_ICON_BG: Record<Tone, string> = {
  default: "bg-lime/25 text-ink",
  warning: "bg-amber-100 text-amber-700",
  danger: "bg-rose-100 text-rose-700",
};

function Section<T>({
  title,
  icon: Icon,
  tone,
  count,
  seeAllHref,
  seeAllLabel,
  isLoading,
  isError,
  errorLabel,
  retryLabel,
  onRetry,
  emptyLabel,
  emptyVariant,
  items,
  renderItem,
}: {
  title: string;
  icon: LucideIcon;
  tone: Tone;
  count: number | undefined;
  seeAllHref: string;
  seeAllLabel: string;
  isLoading: boolean;
  isError: boolean;
  errorLabel: string;
  retryLabel: string;
  onRetry: () => void;
  emptyLabel: string;
  emptyVariant: IllustrationVariant;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-2xl border border-smoke bg-paper">
      <header className="flex items-center gap-3 border-b border-smoke px-5 py-4">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full ${TONE_ICON_BG[tone]}`}
        >
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-ink">{title}</h2>
        </div>
        {typeof count === "number" ? (
          <Badge
            tone={
              count === 0
                ? "mist"
                : tone === "danger"
                  ? "danger-soft"
                  : tone === "warning"
                    ? "mist"
                    : "lime-soft"
            }
          >
            {count}
          </Badge>
        ) : null}
        <Link
          to={seeAllHref}
          className="ml-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-ink transition-colors hover:bg-lime/20"
        >
          {seeAllLabel}
          <ArrowRight size={14} />
        </Link>
      </header>

      <div className="p-2">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-sm text-ash">
            <p>{errorLabel}</p>
            <button
              type="button"
              onClick={onRetry}
              className="text-xs font-semibold text-lime-dark underline underline-offset-2 hover:text-ink"
            >
              {retryLabel}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <EmptySectionIllustration variant={emptyVariant} />
            <p className="text-sm text-ash">{emptyLabel}</p>
          </div>
        ) : (
          <ul className="divide-y divide-smoke">
            {items.map((it) => renderItem(it))}
          </ul>
        )}
      </div>
    </section>
  );
}

function PreviewRow({
  to,
  title,
  subtitle,
  meta,
  titleClamp,
}: {
  to: string;
  title: string;
  subtitle: string;
  meta: string;
  titleClamp?: boolean;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-lime/15"
      >
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm font-semibold text-ink ${titleClamp ? "line-clamp-1" : "truncate"}`}
          >
            {title}
          </p>
          <p className="truncate text-xs text-ash">{subtitle}</p>
        </div>
        <span className="whitespace-nowrap text-[11px] text-ash">{meta}</span>
      </Link>
    </li>
  );
}
